/**
 * Migration Registry - Registration and chain resolution.
 *
 * All migration definitions are registered here at module init time.
 * Chain resolution builds the ordered step list for any fromVersion→toVersion pair.
 *
 * INVARIANTS:
 *   - Only one migration per (fromVersion, toVersion) pair is allowed.
 *   - Registering a duplicate throws (fail-fast, caught at import time in tests).
 *   - getChain() resolves the exact sequential path; missing links throw MigrationPathError.
 *   - The registry is domain-scoped: envelope migrations and payload migrations are
 *     registered in separate named scopes, not mixed.
 */

import { MigrationDefinition, MigrationPathError } from "./types";

type MigrationMap = Map<number, MigrationDefinition>;

export class MigrationRegistry {
  private readonly scopes = new Map<string, MigrationMap>();

  private scope(name: string): MigrationMap {
    if (!this.scopes.has(name)) {
      this.scopes.set(name, new Map());
    }
    return this.scopes.get(name)!;
  }

  /**
   * Register a migration in the named scope.
   * Throws if a migration for (scope, fromVersion) already exists.
   */
  register(scopeName: string, def: MigrationDefinition): void {
    const map = this.scope(scopeName);
    if (map.has(def.fromVersion)) {
      throw new Error(
        `MigrationRegistry: duplicate registration for scope="${scopeName}" fromVersion=${def.fromVersion}`,
      );
    }
    map.set(def.fromVersion, def);
  }

  /**
   * Build the ordered migration chain from fromVersion to toVersion.
   *
   * Example: getChain("payload", 1, 3) → [ def(1→2), def(2→3) ]
   *
   * Throws MigrationPathError if any step in the chain is missing.
   * Returns [] if fromVersion === toVersion (no migration needed).
   */
  getChain(scopeName: string, fromVersion: number, toVersion: number): MigrationDefinition[] {
    if (fromVersion === toVersion) return [];
    if (fromVersion > toVersion) {
      throw new Error(
        `MigrationRegistry: downgrade not supported (scope="${scopeName}", from=${fromVersion}, to=${toVersion})`,
      );
    }

    const map   = this.scope(scopeName);
    const chain: MigrationDefinition[] = [];
    let   current = fromVersion;

    while (current < toVersion) {
      const def = map.get(current);
      if (!def) {
        throw new MigrationPathError(fromVersion, toVersion, current);
      }
      chain.push(def);
      current = def.toVersion;
    }

    return chain;
  }

  /**
   * Returns true if there is a registered migration for the given step.
   * Useful for callers that need to check before committing to a migration path.
   */
  hasStep(scopeName: string, fromVersion: number): boolean {
    return this.scope(scopeName).has(fromVersion);
  }

  listTransitions(scopeName: string): { fromVersion: number; toVersion: number }[] {
    const map = this.scopes.get(scopeName);
    if (!map) return [];
    return [...map.values()].map(def => ({ fromVersion: def.fromVersion, toVersion: def.toVersion })).sort((a, b) => a.fromVersion - b.fromVersion);
  }

  /** List all registered scopes (for diagnostics). */
  listScopes(): string[] {
    return [...this.scopes.keys()];
  }

  /** List all registered fromVersions in a scope (for diagnostics). */
  listSteps(scopeName: string): number[] {
    const map = this.scopes.get(scopeName);
    if (!map) return [];
    return [...map.keys()].sort((a, b) => a - b);
  }

  /**
   * Reset the registry (test use only).
   * Should NOT be called in production code.
   */
  _reset(): void {
    this.scopes.clear();
  }
}

/** Singleton registry used by all persistence subsystems. */
export const migrationRegistry = new MigrationRegistry();

/* ─── Scope name constants ────────────────────────────────── */

/** Scope for StorageEnvelope.version transitions. */
export const SCOPE_ENVELOPE = "envelope";

/** Scope for ProjectContentPayload.schemaVersion transitions. */
export const SCOPE_PAYLOAD = "payload";

/** Scope for SyncLedger format transitions. */
export const SCOPE_SYNC_LEDGER = "sync-ledger";

/** Scope for ACUUsageLedger format transitions. */
export const SCOPE_ACU_LEDGER = "acu-ledger";

/** Scope for DetailedSnapshot format transitions. */
export const SCOPE_SNAPSHOT = "snapshot";
